import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';
import styles from './Sheet.module.css';

export type SheetProps = {
  aberto: boolean;
  onFechar: () => void;
  titulo?: string;
  subtitulo?: string;
  largura?: number;
  children: ReactNode;
  rodape?: ReactNode;
};

/**
 * Drawer à direita no desktop, bottom-sheet no mobile (≤768px).
 * Portal em document.body; Esc fecha; trava o scroll do body enquanto aberto.
 */
export default function Sheet({
  aberto,
  onFechar,
  titulo,
  subtitulo,
  largura,
  children,
  rodape
}: SheetProps) {
  // Esc fecha enquanto aberto.
  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aberto, onFechar]);

  // Trava o scroll do body enquanto aberto.
  useEffect(() => {
    if (!aberto) return;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = anterior;
    };
  }, [aberto]);

  if (!aberto) return null;

  return createPortal(
    <div className={styles.overlay} onClick={onFechar}>
      <div
        className={styles.painel}
        style={{ width: largura ?? 480 }}
        role="dialog"
        aria-modal="true"
        onClick={(evento) => evento.stopPropagation()}
      >
        <div className={styles.cabecalho}>
          <div className={styles.titulos}>
            {titulo && <div className={styles.titulo}>{titulo}</div>}
            {subtitulo && <div className={styles.subtitulo}>{subtitulo}</div>}
          </div>
          <Button variante="fantasma" tamanho="sm" icone="close" onClick={onFechar} />
        </div>
        <div className={styles.corpo}>{children}</div>
        {rodape && <div className={styles.rodape}>{rodape}</div>}
      </div>
    </div>,
    document.body
  );
}
